import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CALENDAR_EVENT_AGENDAMENTO,
  IAgendamento,
  ordemDataHora
} from '@app/modules/agendamento/model/agendamento.model';
import { colors } from '@app/modules/agendamento/model/colors_calendario';
import { AgendamentoService } from '@app/modules/agendamento/services/agendamento.service';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  DialogListAgendamentoComponent
} from '@app/shared/components';
import {
  convertDateToMaskTimezone,
  validarAdicaoRaiz,
  verificarRotasDeAcessoRaiz
} from '@app/shared/utils';
import { CalendarEvent, CalendarView } from 'angular-calendar';
import * as moment from 'moment';
import { finalize, Subject } from 'rxjs';
import {
  ErroMessages,
  IErroMessages,
  TokenService
} from 'viggo-sistemas-core-web';

@Component({
  selector: 'dashboard-info',
  templateUrl: './dashboard-info.component.html',
  styleUrls: ['./dashboard-info.component.scss'],
  providers: [DatePipe, AgendamentoService]
})
export class DashboardInfoComponent implements OnInit {
  loading = false;
  filterEntities = [] as IAgendamento[];
  cores = colors;
  agendamentos: CalendarEvent[] = [];
  view: CalendarView;
  CalendarView = CalendarView;
  viewDate: Date = new Date();
  refresh: Subject<any> = new Subject();
  include = [
    // 'paciente.parceiro',
    // 'profissional.parceiro',
    'local_agendamento'
  ];

  constructor(
    public datePipe: DatePipe,
    public dialog: MatDialog,
    public matSnackBar: MatSnackBar,
    public actRoute: ActivatedRoute,
    public tokenService: TokenService,
    public router: Router,
    public entityService: AgendamentoService
  ) {
    this.view = CalendarView.Month;
  }

  ngOnInit(): void {
    this.loading = true;
    this.loadAgendamentos();
  }

  loadAgendamentos() {
    const atualCalendarDate = this.viewDate;
    var ultimoDiaDoMes = new Date(
      atualCalendarDate.getFullYear(),
      atualCalendarDate.getMonth() + 1,
      0
    );
    const primeiroDia =
      atualCalendarDate.getFullYear() +
      '-' +
      (atualCalendarDate.getMonth() + 1) +
      '-' +
      1;
    const ultimoDia =
      atualCalendarDate.getFullYear() +
      '-' +
      (atualCalendarDate.getMonth() + 1) +
      '-' +
      ultimoDiaDoMes.getDate();
    this.entityService
      .getEntitiesByParams(
        {
          where: {
            de: convertDateToMaskTimezone(primeiroDia),
            ate: convertDateToMaskTimezone(ultimoDia)
          } as any,
          include: this.include
        },
        this.tokenService.getDomain().id
      )
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe(
        value => {
          this.filterEntities = value;
          this.calcularCompromissos();
        },
        (err: HttpErrorResponse) => this.displayError(err)
      );
  }

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    if (events.length === 0) return;
    if (date.getMonth() === this.viewDate.getMonth()) {
      const eventoAgendado = events.map(value => {
        if (
          this.datePipe.transform(value.start, 'shortDate') ===
          this.datePipe.transform(date, 'shortDate')
        )
          return value;
      }) as any;
      this.dialog.open(DialogListAgendamentoComponent, {
        data: {
          agendamentos: eventoAgendado.sort(ordemDataHora),
          showBtnDialog: false
        },
        disableClose: false,
        panelClass: [
          'custom-container-visible',
          'position-relative',
          'max-w-100-vh'
        ]
      });
    } else {
      this.matSnackBar.open('Vá para o mês atual', 'X', {
        duration: 2000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['msg-error']
      });
    }
  }

  /**
   * Responsavel por calcular os grupos de eventos
   * a ser mostrado o badge em cada dia
   * @param events evento a ser pesquisado
   */
  groupEvents(events: CalendarEvent[] | any): CALENDAR_EVENT_AGENDAMENTO[] {
    return events
      .map((event, idx) => {
        return {
          color: colors[event.status].primary,
          paciente: event.nome_paciente,
          local_agendamento: event.local_agendamento.descricao,
          data_hora: event.data_hora,
          total: idx + 1
        } as CALENDAR_EVENT_AGENDAMENTO;
      })
      .filter(e => e.total !== 0)
      .sort(ordemDataHora);
  }

  /**
   * Calcula a frequencia de eventos com base nos eventos imutavéis
   * @param forcarRecalculamento força o recalculamento das rotas
   */
  calcularCompromissos(forcarRecalculamento = false) {
    const atualCalendarDate = this.viewDate;

    const primeiroDiaDoMes = new Date(
      atualCalendarDate.getFullYear(),
      atualCalendarDate.getMonth(),
      1
    );
    const ultimoDiaDoMes = new Date(
      atualCalendarDate.getFullYear(),
      atualCalendarDate.getMonth() + 1,
      0
    );

    if (
      !forcarRecalculamento &&
      this.agendamentos[0] &&
      atualCalendarDate.getMonth() === this.agendamentos[0].start.getMonth()
    ) {
      return;
    }

    const contadorEventos: {
      data_hora: Date;
      agendamento: IAgendamento[];
    }[] = [];

    let auxDiaAtual = primeiroDiaDoMes;

    while (auxDiaAtual <= ultimoDiaDoMes) {
      contadorEventos.push({
        data_hora: auxDiaAtual,
        agendamento: [...this.quantidadeAgendamentos(auxDiaAtual)]
      });
      auxDiaAtual = moment(auxDiaAtual)
        .add({ days: 1 })
        .toDate();
    }
    this.agendamentos = contadorEventos
      .map((contador): CalendarEvent[] => {
        const agendamentos: CalendarEvent[] = contador.agendamento.map(
          agendado => ({
            title: agendado.nome_paciente,
            start: contador.data_hora,
            color: colors.CRIADO,
            ...agendado
          })
        );
        return [...agendamentos];
      })
      .flat();
  }

  /**
   * Encontros diarias para visita com base em uma data
   * @param dataCalendario data a ser testada
   */
  public quantidadeAgendamentos(dataCalendario: Date): IAgendamento[] {
    const agendamentos: IAgendamento[] = [];
    this.filterEntities.forEach(c => {
      if (
        this.datePipe.transform(c.data_hora, 'shortDate') ===
        this.datePipe.transform(dataCalendario, 'shortDate')
      ) {
        agendamentos.push({ ...c });
      }
    });
    return agendamentos;
  }

  showAddDialog() {
    this.router.navigate([`/agendamentos/new/recepcao`]);
  }

  checkDisponibilidade(method: string, params = '', url: string) {
    const routesAcess = verificarRotasDeAcessoRaiz(
      this.tokenService.getRoutes(),
      url
    );
    return validarAdicaoRaiz(routesAcess, method, params, url);
  }

  displayError(err: HttpErrorResponse, showDefaultMsg = false) {
    const txtMsg = showDefaultMsg
      ? `Identificamos o erro ${err.status} - ${err.statusText} no processamento dos dados!`
      : err.error;

    const errorMsg = new ErroMessages({
      route: this.router.url,
      router: this.router,
      matSnackBar: this.matSnackBar,
      matDialog: this.dialog,
      httpError: err,
      showDefaultMsg: showDefaultMsg,
      loginService: null,
      tokenService: this.tokenService,
      vlMsgDefault: txtMsg,
      dialogComponent: ConfirmDialogComponent,
      dialogData: {
        config: {
          data: {
            title: 'Aviso',
            icon: 'pi pi-fw pi-info-circle text-orange-500 mb-2 mr-2',
            bodyMessage: txtMsg,
            singleBtn: true
          } as ConfirmDialogData,
          width: '500px'
        }
      }
    } as IErroMessages);
    errorMsg.checkError();
  }
}
